import { Grid, GridColumn, GridRow, TabBar, ValueField } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useDeputateLessons } from '../../../../components/Lessons/hooks/useDeputateLessons';
import { DeputateLessonsTable } from '../../../../components/Lessons/Tables/DeputateLessonsTable';
import { OpenLessonsTable } from '../../../../components/Lessons/Tables/Teachers/OpenLessonsTable';
import { OverbookedTeachersTable } from '../../../../components/Lessons/Tables/Teachers/OverbookedTeachersTable';
import { TeacherStats } from '../../../../components/Lessons/Tables/Teachers/TeacherStats';
import { Page } from '../../../../components/Page/Page';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useConfirm } from '../../../../hooks/useConfirm';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../../../hooks/useSelectedSchoolYear';
import { use_ContractsQuery, useAdditionalTeachingLoadsListQuery } from '../../../../types/planung-graphql-client-defs';
import { sumUpArray } from '../../../../utils/arrayFunc';
import { hexToColorOption } from '../../../../utils/colorUtils';

export const LessonTeacherDetailSubpage = observer(() => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const schoolYear = useSelectedSchoolYear();
  const { teacherUuid } = useParams();
  const additionalTeachingLoadsContext = useMemorizedCacheTag('ADDITIONAL_TEACHINGLOAD');
  const permissions = usePermissionChecker();

  const navigate = useNavigate();

  const [assignLoading, setAssignLoading] = useState<boolean>(false);

  const { lessons: lessonsData, assignLessons } = useDeputateLessons({
    organizationUuid: pimAuthClaims.getOrganizationUuid(),
    schoolYearUuid: schoolYear?.uuid ?? '',
  });

  const { teacherData } = useLoadBasicData({ pause: !schoolYear || !teacherUuid });

  const [{ data: contractsData }] = use_ContractsQuery({
    pause: !teacherData,
  });

  const teacher = teacherData?.people.find((p) => p.uuid === teacherUuid);

  const currentQualifications = teacher?.qualifications.map((q) => q.subjectUuid);

  const filteredLessons = lessonsData.filter((lesson) => {
    return teacherUuid ? lesson.teacherUuids.includes(teacherUuid) : false;
  });

  const { html, label } = hexToColorOption(teacher?.timetableConfig?.color ?? '');

  const lessonDeputate = teacher
    ? lessonsData
        .map((lesson) => lesson.teachingLoadInfo?.deputatByTeacher?.get(teacher?.uuid) ?? 0)
        .reduce((sum, dep) => sum + dep, 0)
    : 0;

  const currentContract = contractsData?.contracts.find((c) => c.personUuid === teacher?.uuid);

  const [{ data: additionalTeachingLoadsData }] = useAdditionalTeachingLoadsListQuery({
    variables: {
      schoolyear: schoolYear?.uuid ?? '',
      teacherUuid: teacherUuid,
    },
    context: additionalTeachingLoadsContext,
    pause: !schoolYear,
  });

  const deputateManually =
    sumUpArray(additionalTeachingLoadsData?.additionalTeachingLoads?.map((a) => a.hours) ?? []) ?? 0;

  const { confirm, ConfirmationDialog } = useConfirm({
    defaultTitle: t('lesson.assign'),
  });

  async function handleAssign(lessonUuid: string, teacherUuid: string) {
    const lesson = lessonsData.find((l) => l.uuid === lessonUuid);
    const lessonInfo = lesson?.teachingLoadInfo;
    // TODO: Work in Progress, what is contextUuid?!
    const uuid = 'contextUuid';

    // relevant deputate
    const td = lessonInfo?.deputatByTeacher.get(uuid) ?? 0;
    const cd = lessonInfo?.deputatByClass.get(uuid) ?? 0;
    const d = lessonInfo?.deputat ?? 0;

    const Body: ReactNode = (
      <Grid useFormGap>
        <GridRow spacingTop='s'>{t('lesson.assignLessonToPerson', { name: teacher?.displayName })}</GridRow>
        <GridRow spacingBottom='s'>
          <GridColumn width={4}>
            <ValueField title={t('lesson.table.subject')}>{lesson?.subject}</ValueField>
          </GridColumn>
          <GridColumn width={5}>
            <ValueField title={t('lesson.table.classesGroups')}>{lesson?.groupClassNames}</ValueField>
          </GridColumn>
          <GridColumn width={3}>
            <ValueField title={t('deputate.title', { count: 1 })}>{(td + cd + d).toFixed(2)}</ValueField>
          </GridColumn>
        </GridRow>
      </Grid>
    );

    const result = await confirm({ message: Body });
    if (!result) return;
    setAssignLoading(true);
    await assignLessons([lessonUuid], [{ uuid: teacherUuid }]);
    setAssignLoading(false);
  }

  const handleQualification = (teacherUuid: string) => {
    navigate(
      `/${t('routes.deputate.slug')}/${t('routes.setup.slug')}/${t('routes.teacher.slug')}?personUuid=${teacherUuid}`,
    );
  };

  return (
    <Page
      isForbidden={!permissions?.canReadDeputate()}
      title={teacher?.fullName}
      colorHint={{ color: html, colorLabel: label }}
    >
      <Grid>
        <GridRow>
          <GridColumn width={4}>
            <TeacherStats
              deputateLesson={lessonDeputate}
              deputateManually={deputateManually}
              deputateContract={currentContract?.hoursWeekly ?? 0}
              billingMethod={currentContract?.billingMethod}
              limitedToFullTime={currentContract?.limitedToFullTime}
              fullTimeHours={schoolYear?.fullTimeHours}
            />
          </GridColumn>
          <GridColumn width={8}>
            <div style={{ width: '100%' }}>
              <TabBar
                tabs={[
                  {
                    trigger: t('lesson.unassigned'),
                    id: 'lessons',
                    content: (
                      <OpenLessonsTable
                        teacherUuid={teacherUuid ?? ''}
                        qualifications={currentQualifications ?? []}
                        loading={assignLoading}
                        onAssign={handleAssign}
                        onAddQualification={handleQualification}
                        readonly={!permissions?.canWriteDeputate()}
                      />
                    ),
                  },
                  {
                    trigger: t('persons.overbooked'),
                    id: 'teachers',
                    content: (
                      <OverbookedTeachersTable
                        teacherUuid={teacherUuid ?? ''}
                        qualifications={currentQualifications ?? []}
                      />
                    ),
                  },
                ]}
              />
            </div>
          </GridColumn>
        </GridRow>

        <GridRow>
          <DeputateLessonsTable
            configKey='lessons-table-teachers'
            lessonsData={filteredLessons}
            teacherUuid={teacherUuid}
            defaultInitialValues={{
              teachingLoadEnabled: true,
              teachers: [
                {
                  present: true,
                  teachingLoad: true,
                  writesCertificate: false,
                  person: {
                    uuid: teacher?.uuid ?? '',
                    fullName: teacher?.fullName ?? '',
                    displayNameShort: teacher?.displayNameShort ?? '',
                    color: teacher?.timetableConfig?.color ?? '',
                  },
                },
              ],
            }}
          />
        </GridRow>
      </Grid>

      <ConfirmationDialog />
    </Page>
  );
});
